import { Icon, ProviderLogo } from "@deligoo/ui"
import { IconName } from "@deligoo/ui/src/icons/build"
import clsx from "clsx"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { useAppSelector } from "@/App"
import { AsidePanel } from "@/components/AsidePanel"
import { Card } from "@/components/Card"
import { selectOrderById } from "@/store/orders"
import { Order } from "@/types"
import { formatDistance, formatPrice, formatTime, OrderUtils } from "@/utils"

import cls from "../OrderContextMenu.module.scss"

type OrderDetailsPanelProps = {
  orderId: Order["id"]
  handleClose: () => void
}

export const OrderDetailsPanel = ({ orderId, handleClose }: OrderDetailsPanelProps) => {
  const order = useAppSelector(selectOrderById(orderId))

  const { t } = useTranslation()

  if (!order) return null

  const estimatedDeliveryTime = OrderUtils.getEstimatedDeliveryTime(order)
  const realDeliveryTime = OrderUtils.getRealDeliveryTime(order)

  const driverLate = OrderUtils.getOrderDriverLate(order)
  const clientLate = OrderUtils.getOrderClientLate(order)

  return (
    <AsidePanel handleClose={handleClose} className={cls.detailsPanel}>
      <Card className={cls.detailsPanelCard}>
        <Card.Section className="d-flex justify-between">
          <div className="h400">Szczegóły</div>
          <Card.Close handleClose={handleClose} />
        </Card.Section>

        <Card.Section>
          <div className="h300">Szczegóły zamówienia</div>

          <ItemsList>
            <Item title="Nazwa klienta" iconName="business-client">
              {order.pickup?.name || order.delivery?.name || "-"}
            </Item>
            <Item title="Dystans" iconName="started">
              {formatDistance(OrderUtils.getRealOrderDistance(order))}
            </Item>
            <Item title="Samochód" iconName="private-car">
              {formatDistance(order.distance_for_car)}
            </Item>
            <Item title="Rower" iconName="bike">
              {formatDistance(order.distance_for_bicycle)}
            </Item>
            <Item title="Skuter" iconName="scooter">
              {formatDistance(order.distance_for_scooter)}
            </Item>
            <Item title="Rodzaj" iconName="box">
              {Object.entries(order.packages).map(([type, amount]) => (
                <div className="text-right" key={type}>
                  {t(`orders:package_type.${type}`)}: {amount} szt.
                </div>
              ))}
            </Item>
            <Item title="Waga" iconName="weighing">
              {order.weight} kg
            </Item>
            <Item title="Źródło" iconName="home">
              {order.provider !== "deligoo" ? (
                <ProviderLogo provider={order.provider} marketplace={order.marketplace_kind} className={cls.provider} />
              ) : (
                "-"
              )}
            </Item>
            <Item title="Środek transportu" iconName="delivery-car">
              {order.transport_types.map((transportType) => t(`drivers:${transportType}`).toLowerCase()).join(", ")}
            </Item>
            <Item title="Uwagi do zamówienia" iconName="envelope" column={Boolean(order.client_comment)}>
              {order.client_comment || "-"}
            </Item>
          </ItemsList>
        </Card.Section>

        <Card.Section>
          <div className="h300">Parametry czasowe</div>

          <ItemsList>
            <Item title="Czas dodania" iconName="box-new">
              {formatTime(order.created_at)}
            </Item>
            <Item title="Czas kompletacji" iconName="box-check">
              {OrderUtils.getPickingTime(order)}
            </Item>
            <Item title="Czas publikacji" iconName="box-check">
              {formatTime(order.publish_at) || "-"}
            </Item>
          </ItemsList>
        </Card.Section>

        <Card.Section>
          <div className="h300">Czasy estymowane | Czasy rzeczywiste</div>

          <ItemsList>
            <Item title="Czas odbioru" iconName="box-pickup">
              {formatTime(order.pickup?.estimated_time) || "-"}
              {" | "}
              {formatTime(order.pickup?.finished_at) || "-"}
            </Item>
            <Item title="Czas doręczenia" iconName="box-delivery">
              {formatTime(order.delivery?.estimated_time) || "-"}
              {" | "}
              {formatTime(order.delivery?.finished_at) || "-"}
            </Item>
            <Item title="Czas przewozu" iconName="delivery-time">
              {estimatedDeliveryTime !== null ? `${estimatedDeliveryTime} min.` : "–"}
              {" | "}
              {realDeliveryTime !== null ? `${realDeliveryTime} min.` : "-"}
            </Item>
          </ItemsList>
        </Card.Section>

        <Card.Section>
          <div className="h300">Spóźnienia</div>

          <ItemsList>
            <Item title="Spóźnienie kuriera" iconName="delivery-time">
              {driverLate !== null ? `${driverLate} min.` : "-"}
            </Item>
            <Item title="Spóźnienie lokalizacji" iconName="clock">
              {clientLate !== null ? `${clientLate} min.` : "-"}
            </Item>
          </ItemsList>
        </Card.Section>

        <Card.Section>
          <div className="h300">Płatności</div>

          <ItemsList>
            <Item title="Wartość zamówienia" iconName="invoice">
              {order.price_subunit !== null ? `${formatPrice(order.price_subunit, { subunit: true })} zł` : "-"}
            </Item>
            <Item title="Wartość kosztu dostawy" iconName="delivery-time">
              {order.price_delivery_subunit !== null
                ? `${formatPrice(order.price_delivery_subunit, { subunit: true })} zł`
                : "-"}
            </Item>
            <Item title="Wartość rabatu" iconName="sale">
              {order.discount_code_value_subunit
                ? `${formatPrice(order.discount_code_value_subunit, { subunit: true })} zł`
                : "-"}
            </Item>
            <Item title="Suma zamówienia" iconName="money-bay">
              {formatPrice(order.full_price_subunit, { subunit: true })} zł
            </Item>
          </ItemsList>
        </Card.Section>

        <Card.Section>
          <div className="h300">Koszty</div>

          <ItemsList>
            <Item title="Wartość kosztu realizacji" iconName="bills">
              {order.delivery_cost_subunit !== null
                ? `${formatPrice(order.delivery_cost_subunit, { subunit: true })} zł netto`
                : "-"}
            </Item>
            <Item title="Wartość kosztów dodatkowych" iconName="bills">
              {order.additional_cost_subunit !== null
                ? `${formatPrice(order.additional_cost_subunit, { subunit: true })} zł`
                : "-"}
            </Item>
          </ItemsList>
        </Card.Section>
      </Card>
    </AsidePanel>
  )
}

const ItemsList = ({ children }: { children: ReactNode }) => {
  return <dl className={cls.itemsList}>{children}</dl>
}

type ItemProps = {
  iconName: IconName
  title: string
  column?: boolean
  children: ReactNode
}

const Item = ({ iconName, title, column, children }: ItemProps) => {
  return (
    <div className={clsx(cls.listItem, column && cls.column)}>
      <Icon name={iconName} color="primary" className="mr-2 flex-shrink-0" />
      <dt className={cls.listTitle}>{title}</dt>
      <dd className={cls.listDesc}>{children}</dd>
    </div>
  )
}
